import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { createInitialFormData, FormData } from '../types/form';
import { getSubmitWebhookUrl } from '../data/config';
import { formatPermissionMappingForEmail, mapFormToPermissions } from '../data/permissionMapper';
import { DOCUMENT_CAPABILITIES } from '../data/referenceData';
import { createSavedRequest, getSavedRequest, upsertSavedRequest } from '../data/savedRequests';
import { useAuth } from '../context/AuthContext';
import { useReferenceData } from '../context/ReferenceDataContext';
import { DcSessionBanner } from '../components/RequireAuth';
import { StepIndicator } from '../components/StepIndicator';
import { AboutGroupStep } from '../components/steps/AboutGroupStep';
import { SubgroupsStep } from '../components/steps/SubgroupsStep';
import { DocumentsStep } from '../components/steps/DocumentsStep';
import { LegalAICopilotStep } from '../components/steps/LegalAICopilotStep';
import { LegalHelplineStep } from '../components/steps/LegalHelplineStep';
import { MarketplaceStep } from '../components/steps/MarketplaceStep';
import { ClaimsKnowledgeStep } from '../components/steps/ClaimsKnowledgeStep';
import { GeneralSupportStep } from '../components/steps/GeneralSupportStep';
import { OtherStep } from '../components/steps/OtherStep';
import { ReviewStep } from '../components/steps/ReviewStep';
import { SuccessScreen } from '../components/SuccessScreen';
import { ArrowLeftIcon, ChevronLeftIcon, ChevronRightIcon, LogOutIcon } from 'lucide-react';
const STEPS = ['About', 'Subgroups', 'Documents', 'Copilot', 'Helpline', 'Marketplace', 'Claims & KC', 'General', 'Other', 'Review'];
const SUBMITTER_KEY = 'lawvo-submitter-info';
function loadSubmitter(): {
  name: string;
  email: string;
} | null {
  try {
    const raw = localStorage.getItem(SUBMITTER_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}
export function GroupSetupForm() {
  const {
    id: routeId
  } = useParams<{
    id: string;
  }>();
  const navigate = useNavigate();
  const isNew = routeId === 'new';
  const {
    user,
    logout
  } = useAuth();
  const refs = useReferenceData();
  const savedSubmitter = loadSubmitter();
  const [requestId, setRequestId] = useState<number | null>(isNew ? null : Number(routeId) || null);
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState<FormData>(() => createInitialFormData(savedSubmitter || (user ? {
    name: [user.firstName, user.lastName].filter(Boolean).join(' ') || user.email,
    email: user.email
  } : null)));
  const [requestStatus, setRequestStatus] = useState<'draft' | 'submitted'>('draft');
  const [isLoaded, setIsLoaded] = useState(isNew);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  useEffect(() => {
    if (isNew) {
      setIsLoaded(true);
      return;
    }
    if (!routeId || Number.isNaN(Number(routeId))) {
      navigate('/', {
        replace: true
      });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const saved = await getSavedRequest(routeId);
        if (cancelled) return;
        if (!saved) {
          navigate('/', {
            replace: true
          });
          return;
        }
        setRequestId(saved.id);
        setFormData(saved.formData);
        setCurrentStep(saved.currentStep);
        setRequestStatus(saved.status);
        setIsLoaded(true);
      } catch (err: unknown) {
        if (!cancelled) {
          setLoadError(err instanceof Error ? err.message : 'Failed to load request');
          setIsLoaded(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isNew, routeId, navigate]);
  const updateFormData = (updates: Partial<FormData>) => {
    setFormData(prev => ({
      ...prev,
      ...updates
    }));
    setErrors(prev => {
      const next = {
        ...prev
      };
      Object.keys(updates).forEach(key => delete next[key]);
      return next;
    });
  };
  const validateStep = (step: number): boolean => {
    const newErrors: Record<string, string> = {};
    switch (step) {
      case 0:
        if (!formData.submitterName.trim()) newErrors.submitterName = 'Your name is required';
        if (!formData.submitterEmail.trim()) {
          newErrors.submitterEmail = 'Your email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.submitterEmail.trim())) {
          newErrors.submitterEmail = 'Please enter a valid email address';
        }
        if (!formData.groupDescription.trim()) {
          newErrors.groupDescription = 'Group description is required';
        }
        break;
      case 1:
        if (formData.needSubgroups === null) newErrors.needSubgroups = 'Please select an option';else if (formData.needSubgroups && !formData.subgroupLevels) {
          newErrors.subgroupLevels = 'Please select the number of levels';
        }
        break;
      case 2:
        if (!formData.documentAllTags && formData.documentTemplateTagIds.length === 0) {
          newErrors.documentTemplateTagIds = 'Please select at least one template group';
        }
        if (!formData.documentAllServices && formData.documentServiceIds.length === 0) {
          newErrors.documentServiceIds = 'Please select at least one service';
        }
        if (formData.documentCapabilities.length === 0) {
          newErrors.documentCapabilities = 'Please select at least one capability';
        }
        break;
      case 3:
        if (formData.hasLegalAICopilot === null) {
          newErrors.hasLegalAICopilot = 'Please select an option';
        } else if (formData.hasLegalAICopilot) {
          if (!formData.copilotAllProvinces && formData.copilotProvinceIds.length === 0) {
            newErrors.copilotProvinceIds = 'Please select at least one province or All';
          }
          if (!formData.copilotAllPracticeAreas && formData.copilotPracticeAreaIds.length === 0) {
            newErrors.copilotPracticeAreaIds = 'Please select at least one practice area or All';
          }
        }
        break;
      case 4:
        if (formData.hasHelpline === null) newErrors.hasHelpline = 'Please select an option';else if (formData.hasHelpline) {
          if (formData.hasAccessToIntakeForm === null) {
            newErrors.hasAccessToIntakeForm = 'Please select an option';
          }
          if (!formData.helplineMaskNumber.trim()) {
            newErrors.helplineMaskNumber = 'Display number is required';
          }
          if (!formData.helplineRealNumber.trim()) {
            newErrors.helplineRealNumber = 'Actual number is required';
          }
        }
        break;
      case 5:
        if (formData.hasGetAQuote === null) newErrors.hasGetAQuote = 'Please select an option';
        break;
      case 6:
        if (formData.claimsSubmit === null) newErrors.claimsSubmit = 'Please select an option';
        if (formData.hasKnowledgeCenter === null) {
          newErrors.hasKnowledgeCenter = 'Please select an option';
        }
        break;
      case 7:
        if (formData.supportAccess === null) {
          newErrors.supportAccess = 'Please select an option';
        }
        break;
      default:
        break;
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const persistRequest = async (status: 'draft' | 'submitted' = requestStatus): Promise<number> => {
    if (!requestId) {
      const created = await createSavedRequest(formData, currentStep);
      setRequestId(created.id);
      setRequestStatus(created.status);
      navigate(`/setup/${created.id}`, {
        replace: true
      });
      return created.id;
    }
    const updated = await upsertSavedRequest({
      id: requestId,
      formData,
      currentStep,
      status,
      submittedAt: status === 'submitted' ? new Date().toISOString() : undefined
    });
    setRequestStatus(updated.status);
    return updated.id;
  };
  const handleNext = () => {
    if (!validateStep(currentStep)) return;
    if (currentStep === 0) {
      try {
        localStorage.setItem(SUBMITTER_KEY, JSON.stringify({
          name: formData.submitterName,
          email: formData.submitterEmail
        }));
      } catch {
        // ignore
      }
    }
    setCurrentStep(prev => Math.min(prev + 1, STEPS.length - 1));
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 0));
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  const generateEmailHtml = (data: FormData, permissionHtml: string): string => {
    const section = (title: string, rows: string) => `<table width="100%" cellpadding="0" cellspacing="0" style="margin-bottom:24px;border:1px solid #E8EEF4;border-radius:8px;overflow:hidden;">
        <tr><td style="background:#F6F9FC;padding:12px 16px;border-bottom:1px solid #E8EEF4;">
          <strong style="color:#101D5B;font-size:15px;">${title}</strong>
        </td></tr>
        <tr><td style="padding:16px;"><table width="100%" cellpadding="0" cellspacing="0">${rows}</table></td></tr>
      </table>`;
    const row = (label: string, value: string) => `<tr><td style="padding:6px 0;color:#7e8299;font-size:13px;width:40%;vertical-align:top;">${label}</td>
      <td style="padding:6px 0;color:#101D5B;font-size:13px;font-weight:500;">${value || '<em style="color:#C8CBD8;">None</em>'}</td></tr>`;
    const capLabels = data.documentCapabilities.map(k => DOCUMENT_CAPABILITIES.find(c => c.key === k)?.label || k).join(', ');
    let html = `<!DOCTYPE html><html><head><meta charset="utf-8"></head>
<body style="font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;background:#F6F9FC;margin:0;padding:32px 16px;">
<div style="max-width:600px;margin:0 auto;background:#ffffff;border-radius:12px;border:1px solid #E8EEF4;overflow:hidden;">
<div style="background:#101D5B;padding:24px;text-align:center;">
<img src="https://cdn.magicpatterns.com/uploads/3ftRyWK2KRqGfm3CEuTyHF/logo.png" alt="Lawvo" style="height:40px;" />
<h1 style="color:#ffffff;font-size:20px;margin:12px 0 0;">Group Setup Request</h1>
</div><div style="padding:24px;">`;
    html += section('Submitted By', row('Name', data.submitterName) + row('Email', data.submitterEmail));
    html += section('About the Group', row('Description', data.groupDescription));
    let subRows = row('Needs subgroups', data.needSubgroups ? 'Yes' : 'No');
    if (data.needSubgroups) {
      subRows += row('Levels allowed', data.subgroupLevels === 3 ? '3+ levels' : `${data.subgroupLevels} level(s)`);
    }
    subRows += row('Invitation limit', data.numberOfUsers === '' ? 'Not specified' : String(data.numberOfUsers));
    html += section('Subgroups & Users', subRows);
    html += section('Documents', row('Capabilities', capLabels) + row('Docs per month', data.docsPerMonth ? String(data.docsPerMonth) : 'Unlimited') + row('Docs in progress', data.docsInProgress ? String(data.docsInProgress) : 'Unlimited'));
    html += section('Legal AI Copilot', row('Access', data.hasLegalAICopilot ? 'Yes' : 'No'));
    let helplineRows = row('Status', data.hasHelpline ? 'On' : 'Off');
    if (data.hasHelpline) {
      helplineRows += row('Intake form access', data.hasAccessToIntakeForm ? 'On' : 'Off') + row('Display number', data.helplineMaskNumber) + row('Actual number', data.helplineRealNumber) + row('Primary email', data.helplinePrimaryEmail);
    }
    html += section('24/7 Legal Helpline', helplineRows);
    html += section('Marketplace', row('Get a quote', data.hasGetAQuote ? 'On' : 'Off'));
    html += section('Claims & Knowledge Center', row('Submit claims', data.claimsSubmit ? 'Yes' : 'No') + row('Knowledge Center', data.hasKnowledgeCenter ? 'Yes' : 'No'));
    html += section('General & Support', row('Support access', data.supportAccess ? 'Yes' : 'No'));
    html += section('Other', row('Notes', data.otherNotes));
    html += permissionHtml;
    html += `</div><div style="background:#F6F9FC;padding:16px 24px;text-align:center;border-top:1px solid #E8EEF4;">
<p style="color:#7e8299;font-size:12px;margin:0;">Submitted via the Lawvo Group Setup Form.</p></div></div></body></html>`;
    return html;
  };
  const handleSubmit = async () => {
    if (!validateStep(currentStep)) return;
    setIsSubmitting(true);
    setSubmitError(null);
    const mapping = mapFormToPermissions(formData);
    const permissionHtml = formatPermissionMappingForEmail(mapping, refs);
    let id: number;
    try {
      id = await persistRequest('draft');
    } catch (err: unknown) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to save before submit');
      setIsSubmitting(false);
      return;
    }
    try {
      const response = await fetch(getSubmitWebhookUrl(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          html: generateEmailHtml(formData, permissionHtml),
          json: formData,
          permissions: mapping.permissions,
          opsNotes: mapping.opsNotes,
          savedRequestId: id
        })
      });
      if (!response.ok) throw new Error(`Server responded with ${response.status}`);
      await upsertSavedRequest({
        id,
        formData,
        currentStep,
        status: 'submitted',
        submittedAt: new Date().toISOString(),
        webhookSubmittedAt: new Date().toISOString()
      });
      setRequestStatus('submitted');
      setIsSubmitted(true);
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    } catch (err: unknown) {
      setSubmitError(err instanceof Error ? err.message : 'Something went wrong.');
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleSave = async () => {
    setIsSaving(true);
    try {
      await persistRequest(requestStatus);
      setSaveMessage('Saved');
      setTimeout(() => setSaveMessage(null), 2000);
    } catch {
      alert('Could not save request.');
    } finally {
      setIsSaving(false);
    }
  };
  if (!isLoaded) {
    return <div className="min-h-screen bg-background flex items-center justify-center" data-id="element-318">
        <div className="w-8 h-8 border-2 border-violet-blue/30 border-t-violet-blue rounded-full animate-spin" data-id="element-319" />
      </div>;
  }
  if (loadError) {
    return <div className="min-h-screen bg-background flex items-center justify-center p-4" data-id="element-320">
        <div className="max-w-md text-center space-y-4" data-id="element-321">
          <p className="text-destructive" data-id="element-322">{loadError}</p>
          <Link to="/" className="text-sm text-violet-blue hover:underline" data-id="element-323">
            Back to all requests
          </Link>
        </div>
      </div>;
  }
  if (isSubmitted) {
    return <SuccessScreen onViewList={() => navigate('/')} onNewRequest={() => navigate('/setup/new')} data-id="element-324" />;
  }
  const stepProps = {
    formData,
    updateFormData,
    errors,
    goToStep: setCurrentStep
  };
  const displayTitle = formData.groupDescription.trim() || 'New Group Setup';
  return <div className="min-h-screen bg-background py-8 px-4 sm:px-6 lg:px-8" data-id="element-325">
      <div className="max-w-2xl mx-auto" data-id="element-326">
        <div className="mb-8 relative" data-id="element-327">
          <div className="flex items-center justify-between mb-4" data-id="element-328">
            <Link to="/" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-id="element-329">
              <ArrowLeftIcon className="w-4 h-4" data-id="element-330" />
              All Requests
            </Link>
            <button type="button" onClick={logout} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-id="element-331">
              <LogOutIcon className="w-4 h-4" data-id="element-332" />
              Sign Out
            </button>
          </div>
          <div className="text-center" data-id="element-333">
            <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/pzhmlekvyppnluh27wy1t9/f3160bd3-c8c6-4ee6-97ae-1dc6fc5bb54a/logo.png" alt="Lawvo" className="h-10 mx-auto mb-3" data-id="element-334" />
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground" data-id="element-335">Group Setup Request</h1>
            <p className="text-sm text-muted-foreground mt-1 truncate max-w-md mx-auto" data-id="element-336">
              {displayTitle}
            </p>
            {requestStatus === 'submitted' && <span className="inline-block mt-2 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800" data-id="element-337">
                Previously Submitted — Edits Will Update Saved Copy
              </span>}
            {user && <p className="text-sm text-muted-foreground mt-1" data-id="element-338">Signed in as {user.email}</p>}
          </div>
        </div>

        <DcSessionBanner data-id="element-339" />

        {refs.loadError && <div className="mb-4 rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive" data-id="element-340">
            {refs.loadError}{' '}
            <button type="button" className="underline" onClick={() => refs.refresh()} data-id="element-341">
              Retry
            </button>
          </div>}

        {refs.loadWarnings.length > 0 && !refs.loadError && <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-id="element-342">
            {refs.loadWarnings.join(' · ')}
          </div>}

        <StepIndicator currentStep={currentStep} steps={STEPS} data-id="element-343" />

        <div className="bg-card border border-border rounded-lg shadow-sm" data-id="element-344">
          <div className="p-6 sm:p-8" data-id="element-345">
            {currentStep === 0 && <AboutGroupStep {...stepProps} data-id="element-346" />}
            {currentStep === 1 && <SubgroupsStep {...stepProps} data-id="element-347" />}
            {currentStep === 2 && <DocumentsStep {...stepProps} data-id="element-348" />}
            {currentStep === 3 && <LegalAICopilotStep {...stepProps} data-id="element-349" />}
            {currentStep === 4 && <LegalHelplineStep {...stepProps} data-id="element-350" />}
            {currentStep === 5 && <MarketplaceStep {...stepProps} data-id="element-351" />}
            {currentStep === 6 && <ClaimsKnowledgeStep {...stepProps} data-id="element-352" />}
            {currentStep === 7 && <GeneralSupportStep {...stepProps} data-id="element-353" />}
            {currentStep === 8 && <OtherStep {...stepProps} data-id="element-354" />}
            {currentStep === 9 && <ReviewStep {...stepProps} data-id="element-355" />}
          </div>

          <div className="bg-muted/30 px-6 py-4 border-t border-border flex flex-col-reverse sm:flex-row items-center justify-between gap-4" data-id="element-356">
            <button type="button" onClick={handleBack} disabled={currentStep === 0} className={`w-full sm:w-auto flex items-center justify-center px-4 py-2 border border-input rounded-md text-sm font-medium ${currentStep === 0 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-muted'}`} data-id="element-357">
              <ChevronLeftIcon className="w-4 h-4 mr-1" data-id="element-358" />
              Back
            </button>

            <div className="flex flex-col sm:flex-row items-center gap-4 w-full sm:w-auto" data-id="element-359">
              <button type="button" onClick={handleSave} disabled={isSaving} className="text-sm font-medium text-muted-foreground hover:text-foreground disabled:opacity-60" data-id="element-360">
                {isSaving ? 'Saving...' : saveMessage ?? 'Save'}
              </button>

              {currentStep === STEPS.length - 1 ? <button type="button" onClick={handleSubmit} disabled={isSubmitting} className="w-full sm:w-auto px-6 py-2 bg-violet-blue text-white rounded-md text-sm font-medium hover:bg-violet-blue/90 disabled:opacity-70" data-id="element-361">
                  {isSubmitting ? 'Submitting...' : 'Submit Request'}
                </button> : <button type="button" onClick={handleNext} className="w-full sm:w-auto flex items-center justify-center px-6 py-2 bg-violet-blue text-white rounded-md text-sm font-medium hover:bg-violet-blue/90" data-id="element-362">
                  Next
                  <ChevronRightIcon className="w-4 h-4 ml-1" data-id="element-363" />
                </button>}
            </div>
          </div>

          {submitError && <div className="px-6 py-3 bg-destructive/10 border-t border-destructive/20" data-id="element-364">
              <p className="text-sm text-destructive text-center" data-id="element-365">{submitError}</p>
            </div>}
        </div>
      </div>
    </div>;
}